/**
 * Etapa 4.0 — dispara geocodeVendorAddress após criar vendors/{uid}.
 * P0: await + retry; falha NÃO bloqueia cadastro, mas propaga resultado.
 */
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, increment } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export type GeocodeVendorAddressInput = {
  cep?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  complemento?: string;
};

export type GeocodeTriggerResult = {
  ok: boolean;
  locationStatus?: string;
  skipped?: boolean;
  reason?: string;
  error?: string;
};

const MAX_CLIENT_RETRIES = 3;
const CALL_TIMEOUT_MS = 20_000;
const BACKOFF_MS = [400, 1200, 2500] as const;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`geocode timeout after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function markGeocodeClientFailure(vendorId: string, message: string): Promise<void> {
  try {
    await setDoc(
      doc(db, 'vendors', vendorId),
      {
        geocodePending: true,
        geocodeLastError: String(message || 'unknown').slice(0, 200),
        geocodeAttempts: increment(1),
        geocodeUpdatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[geocodeVendorAddress] falha ao gravar geocodePending:', e);
  }
}

async function markGeocodeClientSuccess(
  vendorId: string,
  locationStatus: string | undefined
): Promise<void> {
  if (locationStatus !== 'CONFIRMED') return;
  try {
    await setDoc(
      doc(db, 'vendors', vendorId),
      {
        geocodePending: false,
        geocodeLastError: null,
      },
      { merge: true }
    );
  } catch {
    /* não bloqueia */
  }
}

/**
 * Chama a CF geocodeVendorAddress com retry (máx. 3) e backoff.
 * Retorna ok=false em falha de rede/callable; ok=true se a CF respondeu
 * (mesmo com NEEDS_REVIEW / skipped — isso é resultado válido do servidor).
 */
export async function triggerGeocodeVendorAddress(
  vendorId: string,
  address?: GeocodeVendorAddressInput | null
): Promise<GeocodeTriggerResult> {
  if (!vendorId || vendorId.length < 10) {
    return { ok: false, error: 'vendorId inválido' };
  }

  const payload: { vendorId: string; address?: GeocodeVendorAddressInput } = { vendorId };
  if (address && typeof address === 'object') {
    payload.address = address;
  }

  let lastError = 'geocode falhou';

  for (let attempt = 0; attempt < MAX_CLIENT_RETRIES; attempt++) {
    if (attempt > 0) {
      await sleep(BACKOFF_MS[Math.min(attempt - 1, BACKOFF_MS.length - 1)]);
    }

    try {
      const fns = getFunctions(getApp(), 'us-central1');
      const callable = httpsCallable<
        { vendorId: string; address?: GeocodeVendorAddressInput },
        { ok?: boolean; locationStatus?: string; skipped?: boolean; reason?: string }
      >(fns, 'geocodeVendorAddress');

      const res = await withTimeout(callable(payload), CALL_TIMEOUT_MS);
      const data = res.data || {};
      const locationStatus = data.locationStatus;

      console.info('[geocodeVendorAddress] ok', {
        vendorId,
        locationStatus,
        skipped: data.skipped,
        reason: data.reason,
        attempt: attempt + 1,
      });

      await markGeocodeClientSuccess(vendorId, locationStatus);

      return {
        ok: true,
        locationStatus,
        skipped: data.skipped,
        reason: data.reason,
      };
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
      console.warn('[geocodeVendorAddress] tentativa falhou', {
        vendorId,
        attempt: attempt + 1,
        error: lastError,
      });
    }
  }

  await markGeocodeClientFailure(vendorId, lastError);
  return { ok: false, error: lastError, locationStatus: 'PENDING_ADDRESS' };
}
